import { useState, useEffect, useRef } from 'react';
import { Button } from '../../../components/ui';
import Modal from '../../../components/common/modal';
import { PeerOptions } from '../../../hooks/usePeers';
import { useClientMinimalList, useDomains } from '../../../hooks/useClients';
import { useProfileTagGroups } from '../../../hooks/useProfileTagGroups';
import { useTiers } from '../../../hooks/useTiers';
import { use2026TagGroups } from '../../../hooks/useTagGroups';
import { useTags } from '../../../hooks/useTags';
import moment from 'moment';

/** Values that mean "needs review" for each content filter; used together for the master "Needs any content reviewed" control. */
const CONTENT_REVIEW_NEEDS_REVIEW: Pick<
  PeerOptions,
  'profilePhotoApproved' | 'displayNameStatus' | 'aboutMeStatus' | 'backgroundTagApproved'
> = {
  profilePhotoApproved: false,
  displayNameStatus: 'pending',
  aboutMeStatus: 'pending',
  backgroundTagApproved: false,
};

type VisualExperienceFilterState = { audio: boolean; video: boolean };

function isAllContentReviewSelected(f: PeerOptions, exp: VisualExperienceFilterState): boolean {
  return (
    f.profilePhotoApproved === CONTENT_REVIEW_NEEDS_REVIEW.profilePhotoApproved &&
    f.displayNameStatus === CONTENT_REVIEW_NEEDS_REVIEW.displayNameStatus &&
    f.aboutMeStatus === CONTENT_REVIEW_NEEDS_REVIEW.aboutMeStatus &&
    f.backgroundTagApproved === CONTENT_REVIEW_NEEDS_REVIEW.backgroundTagApproved &&
    exp.audio &&
    exp.video
  );
}

function hasAnyContentReviewNeedsReviewFilter(f: PeerOptions): boolean {
  return (
    f.profilePhotoApproved === false ||
    f.displayNameStatus === 'pending' ||
    f.aboutMeStatus === 'pending' ||
    f.backgroundTagApproved === false
  );
}

function hasAnyContentReviewSelection(f: PeerOptions, exp: VisualExperienceFilterState): boolean {
  return hasAnyContentReviewNeedsReviewFilter(f) || exp.audio || exp.video;
}

const FilterModal: React.FC<{
  filters: PeerOptions;
  setFilters: React.Dispatch<React.SetStateAction<PeerOptions>>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ filters, setFilters, isOpen, setIsOpen }) => {
  const { data: tiers } = useTiers();
  const { data: clients } = useClientMinimalList({ statuses: 'active' });
  const { data: domains } = useDomains();
  const { getTagsByKey } = useProfileTagGroups();
  const { allTags: contentTags } = use2026TagGroups();
  const { data: traitTags } = useTags({ include_hidden: true, tag_type: 'trait', limit: 200 });

  const [tempFilters, setTempFilters] = useState<PeerOptions>(filters);
  /** UI-only; serialized via needs_any_content_review_types. */
  const [visualExperienceFilters, setVisualExperienceFilters] = useState({ audio: false, video: false });
  const contentReviewMasterRef = useRef<HTMLInputElement>(null);
  // "Clear All" restores the same defaults as a fresh page load with no URL params
  const initialFilterState: PeerOptions = {
    limit: 25,
    page: 1,
    search: '',
    userType: ['onboardingpeer', 'peer', 'onboardinglistener', 'peerlistener'],
    isPeerActive: ['true'],
    testUsers: ['false'],
    tierId: [],
    ageGroup: [],
    ethnicityTags: [],
    genderTags: [],
    familyTags: [],
    languageTags: [],
    relationshipTags: [],
    spiritualityTags: [],
    contentTagIds: [],
    groupName: '',
    hideIncompletePeers: undefined,
    aboutMeStatus: undefined,
    displayNameStatus: undefined,
    profilePhotoApproved: undefined,
    backgroundTagApproved: undefined,
    clientIds: [],
    domainId: undefined,
    contentCreatedAfter: undefined,
    contentCreatedBefore: undefined,
    gradedByUserIds: [],
    gradedAfter: undefined,
    gradedBefore: undefined,
    needsExperienceApproval: undefined,
    needsExperienceApprovalMediaTypes: [],
    tagIds: [],
  };

  const roleList = [
    { name: 'Peer', id: 'peer' },
    { name: 'Peer Applicant', id: 'onboardingpeer' },
    { name: 'Listener', id: 'peerlistener' },
    { name: 'Listener Applicant', id: 'onboardinglistener' },
  ];
  const tierList = tiers?.map((tier) => ({ name: tier.name, id: tier.id })) ?? [];
  const ageGroupList = ['20s', '30s', '40s', '50s', '60s', '70s+'].map((a) => ({ name: a, id: a }));
  const genderList = getTagsByKey('GENDER') ?? [];
  const ethnicityList = getTagsByKey('RACE/ETHNICITY') ?? [];
  const relationshipList = getTagsByKey('RELATIONSHIP') ?? [];
  const familyList = getTagsByKey('FAMILY') ?? [];
  const languageList = getTagsByKey('LANGUAGE') ?? [];
  const spiritualityList = getTagsByKey('SPIRITUALITY') ?? [];
  const contentTagList = contentTags.map((tag) => ({ name: tag.name, id: tag.id }));
  const traitTagList = (traitTags || []).map((tag) => ({ name: tag.name, id: tag.id }));
  const groupList = (clients || [])
    .filter((c) => c.id !== undefined)
    .map((c) => ({ name: c.name, id: c.id as number }));
  const domainList = (domains || [])
    .filter((d) => d.id !== undefined)
    .map((d) => ({ name: d.name, id: d.id as number }));
  const peerVisibilityList = [
    { name: 'Visible', id: 'true' },
    { name: 'Hidden', id: 'false' },
  ];
  const testUsersList = [
    { name: 'Test Peers', id: 'true' },
    { name: 'Non-test Peers', id: 'false' },
  ];

  const handleCheckboxChange = (filterType: keyof PeerOptions, id: string | number) => {
    const currentValues = (tempFilters[filterType] as Array<string | number>) || [];
    const updatedValues =
      filterType === 'testUsers'
        ? [id]
        : currentValues.includes(id)
          ? currentValues.filter((item) => item !== id)
          : [...currentValues, id];
    setTempFilters((prev) => ({ ...prev, [filterType]: updatedValues, page: 1 }));
  };

  const handleMultiSelectChange = (filterType: keyof PeerOptions, id: string | number) => {
    const currentValues = (tempFilters[filterType] as Array<string | number>) || [];
    const updatedValues = currentValues.includes(id)
      ? currentValues.filter((item) => item !== id)
      : [...currentValues, id];
    setTempFilters((prev) => ({ ...prev, [filterType]: updatedValues, page: 1 }));
  };

  // NOTE: previously used for a groupName text filter; currently unused.

  const applyFilters = () => {
    if (tempFilters.userType?.length === 0) {
      tempFilters.userType = ['onboardingpeer', 'peer', 'onboardinglistener', 'peerlistener'];
    }
    setFilters(tempFilters);
  };

  useEffect(() => {
    if (isOpen) {
      setTempFilters(filters);
    }
  }, [filters, isOpen]);

  useEffect(() => {
    const el = contentReviewMasterRef.current;
    if (!el) return;
    const partial =
      hasAnyContentReviewSelection(tempFilters, visualExperienceFilters) &&
      !isAllContentReviewSelected(tempFilters, visualExperienceFilters);
    el.indeterminate = partial;
  }, [tempFilters, visualExperienceFilters]);

  // Keep the "master" flag in sync with actual selections:
  // - true only when ALL review types are selected
  // - undefined otherwise (so pills reflect the individual toggles)
  useEffect(() => {
    const allSelected = isAllContentReviewSelected(tempFilters, visualExperienceFilters);
    setTempFilters((prev) => {
      const next = allSelected ? true : undefined;
      if (prev.needsAnyContentReview === next) return prev;
      return { ...prev, needsAnyContentReview: next };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tempFilters.profilePhotoApproved,
    tempFilters.displayNameStatus,
    tempFilters.aboutMeStatus,
    tempFilters.backgroundTagApproved,
    visualExperienceFilters.audio,
    visualExperienceFilters.video,
  ]);

  // Wire the audio/video "experience approval" checkboxes to the backend:
  // needs_experience_approval=true + needs_experience_approval_media_types=audio|video.
  useEffect(() => {
    const mediaTypes: Array<'audio' | 'video'> = [];
    if (visualExperienceFilters.audio) mediaTypes.push('audio');
    if (visualExperienceFilters.video) mediaTypes.push('video');
    const enabled = mediaTypes.length > 0;
    setTempFilters((prev) => ({
      ...prev,
      needsExperienceApproval: enabled ? true : undefined,
      needsExperienceApprovalMediaTypes: mediaTypes,
      page: 1,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualExperienceFilters.audio, visualExperienceFilters.video]);

  const toggleNeedsAnyContentReview = () => {
    if (isAllContentReviewSelected(tempFilters, visualExperienceFilters)) {
      setTempFilters((prev) => ({
        ...prev,
        needsAnyContentReview: undefined,
        profilePhotoApproved: undefined,
        displayNameStatus: undefined,
        aboutMeStatus: undefined,
        backgroundTagApproved: undefined,
        hasBackgroundTags: undefined,
        page: 1,
      }));
      setVisualExperienceFilters({ audio: false, video: false });
    } else {
      setTempFilters((prev) => ({
        ...prev,
        needsAnyContentReview: true,
        ...CONTENT_REVIEW_NEEDS_REVIEW,
        page: 1,
      }));
      setVisualExperienceFilters({ audio: true, video: true });
    }
  };

  return (
    <Modal
      isModel={isOpen}
      InnerComponent={
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/40 outline-none focus:outline-none">
          <div className="relative mx-auto my-6 w-full max-w-4xl">
            <div className="flex max-h-[90vh] flex-col rounded-2xl border border-gray-200 bg-white shadow-xl">
              <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-8 py-5">
                <h2 className="text-2xl font-bold text-violet-950">Peer Filters</h2>
                <div className="flex items-center gap-3">
                  <Button
                    variant="primary"
                    type="submit"
                    className="bg-green hover:bg-green/80 h-9 px-5 text-sm font-semibold text-black"
                    onClick={() => {
                      applyFilters();
                      setIsOpen(false);
                    }}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    className="bg-red hover:bg-red/80 h-9 px-5 text-sm font-semibold text-black"
                    onClick={() => {
                      setTempFilters(initialFilterState);
                      setVisualExperienceFilters({ audio: false, video: false });
                    }}
                  >
                    Clear All
                  </Button>
                  <button
                    className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                    type="button"
                    onClick={() => setIsOpen(false)}
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto px-8 py-6">
                {/* Row 1: checkboxes for small fixed lists */}
                <div className="mb-8 grid grid-cols-4 gap-x-8">
                  <FilterSection title="Role / Status">
                    <CheckboxOptions
                      filterList={roleList}
                      queryParam={tempFilters.userType || []}
                      handleChange={(id) => handleCheckboxChange('userType', id)}
                    />
                  </FilterSection>
                  <FilterSection title="Visibility">
                    <CheckboxOptions
                      filterList={peerVisibilityList}
                      queryParam={tempFilters.isPeerActive || []}
                      handleChange={(id) => handleCheckboxChange('isPeerActive', id)}
                    />
                  </FilterSection>
                  <FilterSection title="Test Peer">
                    <CheckboxOptions
                      filterList={testUsersList}
                      queryParam={tempFilters.testUsers ?? []}
                      handleChange={(id) => handleCheckboxChange('testUsers', id)}
                    />
                  </FilterSection>
                  <FilterSection title="Completeness">
                    <div className="flex flex-col gap-2">
                      {[
                        { label: 'Hide Incompletes', value: true },
                        { label: 'Show Incompletes', value: false },
                      ].map(({ label, value }) => (
                        <label key={label} className="flex cursor-pointer items-center gap-2">
                          <input
                            type="radio"
                            name="hideIncompletePeers"
                            checked={tempFilters.hideIncompletePeers === value}
                            onChange={() =>
                              setTempFilters((prev) => ({ ...prev, hideIncompletePeers: value, page: 1 }))
                            }
                            className="h-4 w-4 accent-violet-950"
                          />
                          <span className="text-xs font-medium text-gray-800">{label}</span>
                        </label>
                      ))}
                      {tempFilters.hideIncompletePeers !== undefined && (
                        <button
                          className="text-xs text-gray-400 hover:text-gray-600"
                          onClick={() =>
                            setTempFilters((prev) => ({ ...prev, hideIncompletePeers: undefined, page: 1 }))
                          }
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </FilterSection>
                </div>

                {/* Row 2: Has Background Tag radio */}
                <div className="mb-8 grid grid-cols-3 gap-x-8">
                  <FilterSection title="Has Background Tag">
                    <div className="flex gap-4">
                      {[
                        { label: 'Yes', value: true },
                        { label: 'No', value: false },
                      ].map(({ label, value }) => (
                        <label key={label} className="flex cursor-pointer items-center gap-2">
                          <input
                            type="radio"
                            name="hasBackgroundTags"
                            checked={tempFilters.hasBackgroundTags === value}
                            onChange={() => setTempFilters((prev) => ({ ...prev, hasBackgroundTags: value, page: 1 }))}
                            className="h-4 w-4 accent-violet-950"
                          />
                          <span className="text-xs font-medium text-gray-800">{label}</span>
                        </label>
                      ))}
                      {tempFilters.hasBackgroundTags !== undefined && (
                        <button
                          className="text-xs text-gray-400 hover:text-gray-600"
                          onClick={() => setTempFilters((prev) => ({ ...prev, hasBackgroundTags: undefined, page: 1 }))}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </FilterSection>

                  <FilterSection title="Group">
                    <MultiSelect
                      options={groupList}
                      selected={(tempFilters.clientIds || []) as number[]}
                      onChange={(id) => handleMultiSelectChange('clientIds', id)}
                      placeholder="Select Group(s)"
                    />
                  </FilterSection>

                  <FilterSection title="Domain">
                    <select
                      value={tempFilters.domainId ?? ''}
                      onChange={(e) =>
                        setTempFilters((prev) => ({
                          ...prev,
                          domainId: e.target.value ? Number(e.target.value) : undefined,
                          page: 1,
                        }))
                      }
                      className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-violet-950 focus:outline-none"
                    >
                      <option value="">All Domains</option>
                      {domainList.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </FilterSection>
                </div>

                {/* Content Review */}
                <div className="mb-8">
                  <FilterSection title="Content Review">
                    <div className="flex flex-col gap-2">
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          ref={contentReviewMasterRef}
                          type="checkbox"
                          checked={isAllContentReviewSelected(tempFilters, visualExperienceFilters)}
                          onChange={toggleNeedsAnyContentReview}
                          className="h-4 w-4 rounded border-gray-300 accent-violet-950"
                        />
                        <span className="text-xs font-medium text-gray-800">
                          Needs any content reviewed (selects all review types below)
                        </span>
                      </label>
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={tempFilters.profilePhotoApproved === false}
                          onChange={() =>
                            setTempFilters((prev) => ({
                              ...prev,
                              profilePhotoApproved: prev.profilePhotoApproved === false ? undefined : false,
                              page: 1,
                            }))
                          }
                          className="h-4 w-4 rounded border-gray-300 accent-violet-950"
                        />
                        <span className="text-xs font-medium text-gray-800">Needs Profile Picture Review</span>
                      </label>
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={tempFilters.displayNameStatus === 'pending'}
                          onChange={() =>
                            setTempFilters((prev) => ({
                              ...prev,
                              displayNameStatus: prev.displayNameStatus === 'pending' ? undefined : 'pending',
                              page: 1,
                            }))
                          }
                          className="h-4 w-4 rounded border-gray-300 accent-violet-950"
                        />
                        <span className="text-xs font-medium text-gray-800">Needs Display Name Review</span>
                      </label>
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={tempFilters.aboutMeStatus === 'pending'}
                          onChange={() =>
                            setTempFilters((prev) => ({
                              ...prev,
                              aboutMeStatus: prev.aboutMeStatus === 'pending' ? undefined : 'pending',
                              page: 1,
                            }))
                          }
                          className="h-4 w-4 rounded border-gray-300 accent-violet-950"
                        />
                        <span className="text-xs font-medium text-gray-800">Needs About Me Review</span>
                      </label>
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={tempFilters.backgroundTagApproved === false}
                          onChange={() =>
                            setTempFilters((prev) => ({
                              ...prev,
                              backgroundTagApproved: prev.backgroundTagApproved === false ? undefined : false,
                              page: 1,
                            }))
                          }
                          className="h-4 w-4 rounded border-gray-300 accent-violet-950"
                        />
                        <span className="text-xs font-medium text-gray-800">Needs Background Tags Review</span>
                      </label>
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={visualExperienceFilters.audio}
                          onChange={() => setVisualExperienceFilters((prev) => ({ ...prev, audio: !prev.audio }))}
                          className="h-4 w-4 rounded border-gray-300 accent-violet-950"
                        />
                        <span className="text-xs font-medium text-gray-800">Needs experience approval (audio)</span>
                      </label>
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={visualExperienceFilters.video}
                          onChange={() => setVisualExperienceFilters((prev) => ({ ...prev, video: !prev.video }))}
                          className="h-4 w-4 rounded border-gray-300 accent-violet-950"
                        />
                        <span className="text-xs font-medium text-gray-800">Needs experience approval (video)</span>
                      </label>
                    </div>
                  </FilterSection>
                </div>

                {/* Row 3+: multi-select dropdowns */}
                <div className="grid grid-cols-3 gap-x-8 gap-y-6">
                  <FilterSection title="Tier">
                    <MultiSelect
                      options={tierList}
                      selected={(tempFilters.tierId || []) as number[]}
                      onChange={(id) => handleMultiSelectChange('tierId', id)}
                      placeholder="Select Tier"
                    />
                  </FilterSection>

                  <FilterSection title="Gender">
                    <MultiSelect
                      options={genderList}
                      selected={(tempFilters.genderTags || []) as number[]}
                      onChange={(id) => handleMultiSelectChange('genderTags', id)}
                      placeholder="Select Gender"
                    />
                  </FilterSection>

                  <FilterSection title="Age">
                    <MultiSelect
                      options={ageGroupList}
                      selected={(tempFilters.ageGroup || []) as string[]}
                      onChange={(id) => handleMultiSelectChange('ageGroup', id)}
                      placeholder="Select Age Group"
                    />
                  </FilterSection>

                  <FilterSection title="Family">
                    <MultiSelect
                      options={familyList}
                      selected={(tempFilters.familyTags || []) as number[]}
                      onChange={(id) => handleMultiSelectChange('familyTags', id)}
                      placeholder="Select Family Status"
                    />
                  </FilterSection>

                  <FilterSection title="Language">
                    <MultiSelect
                      options={languageList}
                      selected={(tempFilters.languageTags || []) as number[]}
                      onChange={(id) => handleMultiSelectChange('languageTags', id)}
                      placeholder="Select Language"
                    />
                  </FilterSection>

                  <FilterSection title="Relationship">
                    <MultiSelect
                      options={relationshipList}
                      selected={(tempFilters.relationshipTags || []) as number[]}
                      onChange={(id) => handleMultiSelectChange('relationshipTags', id)}
                      placeholder="Select Relationship Status"
                    />
                  </FilterSection>

                  <FilterSection title="Spirituality">
                    <MultiSelect
                      options={spiritualityList}
                      selected={(tempFilters.spiritualityTags || []) as number[]}
                      onChange={(id) => handleMultiSelectChange('spiritualityTags', id)}
                      placeholder="Select Spirituality"
                    />
                  </FilterSection>

                  <FilterSection title="Ethnicity">
                    <MultiSelect
                      options={ethnicityList}
                      selected={(tempFilters.ethnicityTags || []) as number[]}
                      onChange={(id) => handleMultiSelectChange('ethnicityTags', id)}
                      placeholder="Select Ethnicity"
                    />
                  </FilterSection>

                  <FilterSection title="Has Content Tagged">
                    <MultiSelect
                      options={contentTagList}
                      selected={(tempFilters.contentTagIds || []) as number[]}
                      onChange={(id) => handleMultiSelectChange('contentTagIds', id)}
                      placeholder="Select Content Topic"
                    />
                  </FilterSection>

                  <FilterSection title="Tags (Peer)">
                    <MultiSelect
                      options={traitTagList}
                      selected={(tempFilters.tagIds || []) as number[]}
                      onChange={(id) => handleMultiSelectChange('tagIds', id)}
                      placeholder="Select Tag(s)"
                    />
                  </FilterSection>
                </div>

                {/* Row 4: date/graded filters */}
                <div className="mt-6 grid grid-cols-3 gap-x-8 gap-y-6">
                  <FilterSection title="Content Created (UTC)">
                    <DateTimeRangePicker
                      startValue={tempFilters.contentCreatedAfter}
                      endValue={tempFilters.contentCreatedBefore}
                      onChange={(nextStart, nextEnd) =>
                        setTempFilters((prev) => ({
                          ...prev,
                          contentCreatedAfter: nextStart,
                          contentCreatedBefore: nextEnd,
                          page: 1,
                        }))
                      }
                      placeholder="Select created range"
                    />
                  </FilterSection>

                  <FilterSection title="Graded By (User IDs)">
                    <input
                      type="text"
                      placeholder="e.g. 12,34"
                      value={(tempFilters.gradedByUserIds || []).join(',')}
                      onChange={(e) => {
                        const ids = e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean)
                          .map((s) => Number(s))
                          .filter((n) => Number.isFinite(n));
                        setTempFilters((prev) => ({ ...prev, gradedByUserIds: ids, page: 1 }));
                      }}
                      className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-violet-950 focus:outline-none"
                    />
                  </FilterSection>

                  <FilterSection title="Graded At (UTC)">
                    <DateTimeRangePicker
                      startValue={tempFilters.gradedAfter}
                      endValue={tempFilters.gradedBefore}
                      onChange={(nextStart, nextEnd) =>
                        setTempFilters((prev) => ({ ...prev, gradedAfter: nextStart, gradedBefore: nextEnd, page: 1 }))
                      }
                      placeholder="Select graded range"
                    />
                  </FilterSection>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};

const FilterSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex w-full flex-col">
    <div className="mb-3 border-b border-gray-200 pb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
      {title}
    </div>
    {children}
  </div>
);

const CheckboxOptions: React.FC<{
  filterList: { name: string; id: string | number }[];
  queryParam: Array<string | number>;
  handleChange: (id: string | number) => void;
}> = ({ filterList, queryParam, handleChange }) => (
  <div className="flex flex-col gap-2">
    {filterList.map((item) => (
      <label key={item.id} className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={queryParam.includes(item.id)}
          onChange={() => handleChange(item.id)}
          className="h-4 w-4 rounded border-gray-300 accent-violet-950"
        />
        <span className="text-xs font-medium text-gray-800">{item.name}</span>
      </label>
    ))}
  </div>
);

const MultiSelect: React.FC<{
  options: { name: string; id: string | number }[];
  selected: Array<string | number>;
  onChange: (id: string | number) => void;
  placeholder?: string;
}> = ({ options, selected, onChange, placeholder = 'Select...' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedLabels = options.filter((o) => selected.includes(o.id)).map((o) => o.name);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded border border-gray-300 bg-white px-3 py-2 text-left text-sm hover:border-violet-950 focus:outline-none"
      >
        <span className={`truncate ${selectedLabels.length === 0 ? 'text-gray-400' : 'text-gray-800'}`}>
          {selectedLabels.length === 0
            ? placeholder
            : selectedLabels.length === 1
              ? selectedLabels[0]
              : `${selectedLabels.length} selected`}
        </span>
        <svg
          className={`ml-2 h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 max-h-52 w-full overflow-y-auto rounded border border-gray-200 bg-white shadow-lg">
          {options.map((option) => (
            <label key={option.id} className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-violet-50">
              <input
                type="checkbox"
                checked={selected.includes(option.id)}
                onChange={() => onChange(option.id)}
                className="h-4 w-4 rounded border-gray-300 accent-violet-950"
              />
              <span className="text-sm text-gray-800">{option.name}</span>
            </label>
          ))}
        </div>
      )}

      {selected.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {options
            .filter((o) => selected.includes(o.id))
            .map((o) => (
              <span
                key={o.id}
                className="flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-800"
              >
                {o.name}
                <button
                  type="button"
                  onClick={() => onChange(o.id)}
                  className="ml-0.5 text-violet-500 hover:text-violet-800"
                >
                  ×
                </button>
              </span>
            ))}
        </div>
      )}
    </div>
  );
};

const DateTimeRangePicker: React.FC<{
  startValue?: string;
  endValue?: string;
  onChange: (start?: string, end?: string) => void;
  placeholder?: string;
}> = ({ startValue, endValue, onChange, placeholder = 'Select range' }) => {
  const [open, setOpen] = useState(false);
  const [anchorMonth, setAnchorMonth] = useState(() => {
    const seed = startValue || endValue;
    return seed ? moment(seed).startOf('month') : moment().startOf('month');
  });
  const [pendingStartDay, setPendingStartDay] = useState<moment.Moment | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const format = (v?: string) => (v ? moment(v).format('M/D/YYYY h:mm A') : '');
  const toDateTimeLocal = (m: moment.Moment) => m.format('YYYY-MM-DDTHH:mm');
  const parseLocal = (v?: string) => (v ? moment(v) : null);
  const startMoment = parseLocal(startValue);
  const endMoment = parseLocal(endValue);

  const isInRange = (day: moment.Moment) => {
    if (!startMoment || !endMoment) return false;
    return day.isSameOrAfter(startMoment.clone().startOf('day')) && day.isSameOrBefore(endMoment.clone().endOf('day'));
  };

  const applyDayClick = (day: moment.Moment) => {
    const dayStart = day.clone().startOf('day');

    const startTime = startMoment ? { h: startMoment.hour(), m: startMoment.minute() } : { h: 0, m: 0 };
    const endTime = endMoment ? { h: endMoment.hour(), m: endMoment.minute() } : { h: 23, m: 59 };

    if (!pendingStartDay) {
      setPendingStartDay(dayStart);
      const nextStart = dayStart.clone().hour(startTime.h).minute(startTime.m);
      onChange(toDateTimeLocal(nextStart), endValue);
      return;
    }

    const startDay = pendingStartDay.clone();
    const [minDay, maxDay] = dayStart.isBefore(startDay) ? [dayStart, startDay] : [startDay, dayStart];
    const nextStart = minDay.clone().hour(startTime.h).minute(startTime.m);
    const nextEnd = maxDay.clone().hour(endTime.h).minute(endTime.m);
    setPendingStartDay(null);
    onChange(toDateTimeLocal(nextStart), toDateTimeLocal(nextEnd));
  };

  const label =
    startValue || endValue
      ? `${startValue ? format(startValue) : '…'} - ${endValue ? format(endValue) : '…'}`
      : placeholder;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded border border-gray-300 bg-white px-3 py-2 text-left text-sm hover:border-violet-950 focus:outline-none"
      >
        <span className={`truncate ${startValue || endValue ? 'text-gray-800' : 'text-gray-400'}`}>{label}</span>
        <svg
          className={`ml-2 h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute bottom-full z-50 mb-1 w-full rounded border border-gray-200 bg-white p-3 shadow-lg">
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              className="rounded px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
              onClick={() => setAnchorMonth((m) => m.clone().subtract(1, 'month'))}
            >
              ‹
            </button>
            <div className="text-sm font-semibold text-gray-800">{anchorMonth.format('MMMM YYYY')}</div>
            <button
              type="button"
              className="rounded px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
              onClick={() => setAnchorMonth((m) => m.clone().add(1, 'month'))}
            >
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-gray-500">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
              <div key={d} className="py-1">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {(() => {
              const startGrid = anchorMonth.clone().startOf('month').startOf('week');
              const endGrid = anchorMonth.clone().endOf('month').endOf('week');
              const days: moment.Moment[] = [];
              const cur = startGrid.clone();
              while (cur.isSameOrBefore(endGrid, 'day')) {
                days.push(cur.clone());
                cur.add(1, 'day');
              }
              return days.map((day) => {
                const isOutside = !day.isSame(anchorMonth, 'month');
                const selectedStart = startMoment && day.isSame(startMoment, 'day');
                const selectedEnd = endMoment && day.isSame(endMoment, 'day');
                const inRange = isInRange(day);
                const pending = pendingStartDay && day.isSame(pendingStartDay, 'day');
                const bg =
                  selectedStart || selectedEnd
                    ? 'bg-violet-950 text-white'
                    : pending
                      ? 'bg-violet-200 text-violet-950'
                      : inRange
                        ? 'bg-violet-100 text-violet-950'
                        : 'hover:bg-gray-100';
                return (
                  <button
                    key={day.format('YYYY-MM-DD')}
                    type="button"
                    onClick={() => applyDayClick(day)}
                    className={['h-8 rounded text-sm', bg, isOutside ? 'text-gray-300' : ''].join(' ')}
                    aria-label={day.format('YYYY-MM-DD')}
                  >
                    {day.date()}
                  </button>
                );
              });
            })()}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <div className="text-[11px] font-semibold text-gray-500">Start time</div>
              <input
                type="time"
                value={startMoment ? startMoment.format('HH:mm') : ''}
                onChange={(e) => {
                  if (!startMoment) return;
                  const [h, m] = e.target.value.split(':').map((n) => Number(n));
                  const next = startMoment.clone().hour(h).minute(m);
                  onChange(toDateTimeLocal(next), endValue);
                }}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-violet-950 focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-[11px] font-semibold text-gray-500">End time</div>
              <input
                type="time"
                value={endMoment ? endMoment.format('HH:mm') : ''}
                onChange={(e) => {
                  if (!endMoment) return;
                  const [h, m] = e.target.value.split(':').map((n) => Number(n));
                  const next = endMoment.clone().hour(h).minute(m);
                  onChange(startValue, toDateTimeLocal(next));
                }}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-violet-950 focus:outline-none"
              />
            </div>
          </div>

          <div className="mt-2 flex items-center justify-between">
            <div className="text-xs text-gray-500">
              {pendingStartDay ? 'Select an end date…' : 'Click start date, then end date'}
            </div>
            {(startValue || endValue) && (
              <button
                type="button"
                className="text-xs text-gray-400 hover:text-gray-600"
                onClick={() => {
                  setPendingStartDay(null);
                  onChange(undefined, undefined);
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterModal;
